import { PAYMENT_REJECTION_REASONS } from '@api/b2b';
import { getPortalCatalog, getPortalHome, getPortalMe, listPortalOrders } from '@api/b2bPortal';
import PageHeader from '@components/common/PageHeader';
import { Button } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router';
import styles from '../b2b.module.css';
import { Card, Field, Pill } from '../shared';
import { describeScheme, formatDay, money, termsLabel } from '../utils';


/** What the customer has to do next, what they owe and when, where their latest orders are, and the schemes running for them. */
const ACTION: Record<string, string> = {
    'PI Issued': 'Confirm the proforma invoice',
    'PI Confirmed': 'Pay and upload the proof',
    Shipped: 'Mark the delivery received',
};

const PortalHome: React.FC = () => {
    const navigate = useNavigate();
    const { data: me } = useQuery({ queryKey: ['portal-me'], queryFn: getPortalMe });
    const { data: orders = [] } = useQuery({ queryKey: ['portal-orders'], queryFn: listPortalOrders });
    const { data: catalog } = useQuery({ queryKey: ['portal-catalog'], queryFn: getPortalCatalog });
    const { data: home } = useQuery({ queryKey: ['portal-home'], queryFn: getPortalHome });
    const rejectedIds = new Set((home?.rejected ?? []).map((r) => r.purchaseOrderId));
    // A rejected proof replaces the plain "pay" prompt for that order.
    const todo = orders.filter((o) => ACTION[o.status] && !rejectedIds.has(o.purchaseOrderId));
    const today = new Date().toISOString().slice(0, 10);
    const inTwoDays = new Date(Date.now() + 2 * 86_400_000).toISOString().slice(0, 10);
    const expiringSoon = (o: (typeof orders)[number]) => o.status === 'PI Issued' && !!o.expiryDate && String(o.expiryDate).slice(0, 10) <= inTwoDays;
    const owed = (home?.dues ?? []).reduce((sum, d) => sum + d.outstanding, 0);
    const reasonLabel = (code: string | null) => PAYMENT_REJECTION_REASONS.find((r) => r.value === code)?.label ?? code ?? 'No reason given';
    const open = orders.filter((o) => !['Fulfilled', 'Cancelled'].includes(o.status));

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🛒"
                title={me ? `Welcome, ${me.name}` : 'Welcome'}
                subtitle={me?.segment ? `${me.segment} portal` : 'Order at your prices and schemes'}
                actions={
                    <Button variant={ButtonVariant.ACTION} disabled={me ? !me.orderingOpen : true} onClick={() => navigate('/portal/orders/new')}>
                        New order
                    </Button>
                }
            />
            <div className={styles.layout}>
                <div className={styles.main}>
                    <Card title="Needs you" hint={todo.length || home?.rejected.length ? undefined : 'Nothing is waiting on you.'}>
                        {(home?.rejected ?? []).map((r) => (
                            <div key={`rejected-${r.purchaseOrderId}`} className={styles.between}>
                                <div className={styles.stack}>
                                    <div className={styles.row}>
                                        <span className={styles.fieldValue}>{r.purchaseOrderId}</span>
                                        <Pill label="Payment proof rejected" tone="critical" />
                                        <span className={styles.muted}>{money(r.amount)}</span>
                                    </div>
                                    <span className={styles.error}>
                                        {reasonLabel(r.reason)}
                                        {r.note ? `: ${r.note}` : ''}
                                    </span>
                                </div>
                                <Button size="xs" variant={ButtonVariant.ACTION} onClick={() => navigate(`/portal/orders/${encodeURIComponent(r.purchaseOrderId)}`)}>
                                    Upload again
                                </Button>
                            </div>
                        ))}
                        {todo.map((o) => (
                            <div key={o.purchaseOrderId} className={styles.between}>
                                <div className={styles.row}>
                                    <span className={styles.fieldValue}>{o.purchaseOrderId}</span>
                                    <Pill label={o.status} />
                                    <span className={styles.muted}>{money(o.total)}</span>
                                    {expiringSoon(o) ? (
                                        <span className={styles.error}>
                                            {String(o.expiryDate).slice(0, 10) < today ? 'PI expired' : `PI expires ${formatDay(o.expiryDate)}`}
                                        </span>
                                    ) : null}
                                </div>
                                <Button size="xs" variant={ButtonVariant.ACTION} onClick={() => navigate(`/portal/orders/${encodeURIComponent(o.purchaseOrderId)}`)}>
                                    {ACTION[o.status]}
                                </Button>
                            </div>
                        ))}
                    </Card>
                    <Card title="Recent orders" hint={home?.recent.length ? undefined : 'No orders yet.'}>
                        {(home?.recent ?? []).map((o) => (
                            <div key={o.purchaseOrderId} className={styles.between}>
                                <div className={styles.stack}>
                                    <div className={styles.row}>
                                        <span className={styles.fieldValue}>{o.purchaseOrderId}</span>
                                        <Pill label={o.status} />
                                        <span className={styles.muted}>
                                            {formatDay(o.date)} · {money(o.total)}
                                        </span>
                                    </div>
                                    {o.tracking ? (
                                        <span className={styles.cardHint}>
                                            {o.tracking.transporter} · LR/AWB {o.tracking.trackingNumber} · dispatched {formatDay(o.tracking.dispatchDate)}
                                        </span>
                                    ) : null}
                                </div>
                                <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => navigate(`/portal/orders/${encodeURIComponent(o.purchaseOrderId)}`)}>
                                    Open
                                </Button>
                            </div>
                        ))}
                        {orders.length > 5 ? (
                            <Button size="xs" variant={ButtonVariant.SUBTLE} w="fit-content" onClick={() => navigate('/portal/orders')}>
                                All {orders.length} orders
                            </Button>
                        ) : null}
                    </Card>
                    <Card title="Schemes running for you">
                        {(catalog?.schemes ?? []).length === 0 ? <span className={styles.muted}>No scheme is running for you today.</span> : null}
                        {(catalog?.schemes ?? []).map((s) => (
                            <div key={s.id} className={styles.stack}>
                                <span className={styles.fieldValue}>{s.name}</span>
                                <span className={styles.cardHint}>
                                    {describeScheme(s)}
                                    {s.effectiveTo ? ` · until ${formatDay(s.effectiveTo)}` : ''}
                                </span>
                            </div>
                        ))}
                    </Card>
                    {catalog?.instructions ? <Card title="From the brand" hint={catalog.instructions} /> : null}
                </div>
                <div className={styles.rail}>
                    <Card title="Payments due" hint={home?.dues.length ? undefined : 'Nothing is outstanding.'}>
                        {home?.dues.length ? <Field label="Total outstanding" value={money(owed)} /> : null}
                        {(home?.dues ?? []).map((d) => (
                            <div key={`${d.purchaseOrderId}:${d.label}`} className={styles.between}>
                                <div className={styles.stack}>
                                    <span className={styles.fieldValue}>
                                        {d.purchaseOrderId} · {d.label}
                                    </span>
                                    <span className={d.overdue ? styles.error : styles.cardHint}>
                                        {d.verifying
                                            ? 'Proof uploaded, being verified'
                                            : d.dueDate
                                              ? `${d.overdue ? 'Overdue since' : 'Due'} ${formatDay(d.dueDate)}`
                                              : `Due ${d.dueWhen}`}
                                    </span>
                                </div>
                                <span className={d.overdue ? styles.error : styles.fieldValue}>{money(d.outstanding)}</span>
                            </div>
                        ))}
                    </Card>
                    <Card title="Your account">
                        <div className={styles.fieldGrid}>
                            <Field label="Open orders" value={open.length} />
                            <Field label="Payment terms" value={me ? termsLabel(me.paymentTerms) : '—'} />
                            <Field label="Minimum order" value={me && me.minOrderValue > 0 ? money(me.minOrderValue) : '—'} />
                            <Field label="GSTIN" value={me?.gstin ?? '—'} />
                        </div>
                    </Card>
                    <Card title="Ship to">
                        {(me?.shipTos ?? []).map((s) => (
                            <Field key={s.warehouseId} label={s.name} value={s.address} />
                        ))}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PortalHome;
